import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLanding from './components/DashboardLanding';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useAuth } from './contexts/AuthContext';

// Every route but the landing page is split off. They used to ship in the
// single main bundle, so opening the dashboard downloaded the depot charts,
// the yearly analysis and the category editor as well.
const ExpenseManagement = lazy(() => import('./components/ExpenseManagement'));
const BudgetPage = lazy(() => import('./components/BudgetPage'));
const AppLayout = lazy(() => import('./components/AppLayout'));
const BudgetSummaryCard = lazy(() => import('./components/BudgetSummaryCard'));
const YearlyBudgetAnalysis = lazy(() => import('./components/YearlyBudgetAnalysis'));
const IncomeTab = lazy(() => import('./components/IncomeTab'));
const LoansTab = lazy(() => import('./components/LoansTab'));
const DepotTab = lazy(() => import('./components/DepotTab'));
const SonderausgabenPlanning = lazy(() => import('./components/SonderausgabenPlanning'));
const BudgetSettings = lazy(() => import('./components/BudgetSettings'));
const CategoryManagement = lazy(() => import('./components/CategoryManagement'));
const GesamtuebersichtPage = lazy(() => import('./components/GesamtuebersichtPage'));

// A route chunk is fetched when the route is first opened, so a deploy that
// replaces the files under a tab that is already open makes the next click
// ask for a chunk that no longer exists. Before code splitting the whole app
// was one bundle and this could not happen. One reload picks up the new
// index.html; the sessionStorage flag stops a reload loop when the chunk is
// missing for any other reason.
const RELOADED_KEY = 'miko-chunk-reload';

class ChunkErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    const stale =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk \S+ failed/i.test(error?.message || '');
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(RELOADED_KEY) === '1';
      if (stale && !alreadyReloaded) sessionStorage.setItem(RELOADED_KEY, '1');
    } catch (e) {
      // Private mode or blocked storage: fall through to the message below.
    }
    if (stale && !alreadyReloaded) window.location.reload();
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="miko-card p-6">
            <p className="text-sm miko-text-muted">{this.props.message}</p>
            <button className="miko-btn-primary mt-4" onClick={() => window.location.reload()}>
              {this.props.retryLabel}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Placeholder while a route chunk is in flight. Same shape as the cards it
// replaces so the layout does not jump when the real one arrives.
function RouteFallback() {
  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="miko-card p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-3 bg-primary-100 dark:bg-yoru-300/40 rounded w-1/3"></div>
          <div className="h-8 bg-primary-100 dark:bg-yoru-300/40 rounded w-2/3"></div>
          <div className="h-3 bg-primary-100 dark:bg-yoru-300/40 rounded w-1/2"></div>
        </div>
      </div>
    </div>
  );
}

const THEME_KEY = 'miko-theme';

function useTheme() {
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
  }, [dark]);

  return [dark, () => setDark((d) => !d)];
}

function ThemeToggle({ dark, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="miko-btn-ghost"
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={dark ? 'Sakura' : 'Yoru'}
    >
      {dark ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
}

function App() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { userInfo, logout } = useAuth();
  const [dark, toggleTheme] = useTheme();

  const navLinkClass = ({ isActive }) =>
    isActive ? 'miko-nav-link-active' : 'miko-nav-link';
  const mobileNavLinkClass = ({ isActive }) =>
    isActive ? 'miko-mobile-nav-link-active' : 'miko-mobile-nav-link';

  return (
    <Router>
      <div className="h-screen flex flex-col">
        <nav className="miko-nav shrink-0 z-40">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center">
                  <h1 className="text-xl font-bold miko-heading">{t('app.title')}</h1>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:ml-6 md:flex md:space-x-8">
                  <NavLink to="/" className={navLinkClass} end>{t('nav.dashboard')}</NavLink>
                  <NavLink to="/expenses" className={navLinkClass}>{t('nav.expenses')}</NavLink>
                  <NavLink to="/budget" className={navLinkClass}>{t('nav.budget')}</NavLink>
                </div>
              </div>

              {/* User info + theme + logout */}
              <div className="hidden md:flex items-center space-x-3">
                <LanguageSwitcher />
                <ThemeToggle dark={dark} onToggle={toggleTheme} />
                <span className="text-sm miko-text">{userInfo.name}</span>
                <button onClick={logout} className="miko-btn-secondary py-1.5 text-sm">
                  {t('nav.logout')}
                </button>
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center space-x-2">
                <LanguageSwitcher />
                <ThemeToggle dark={dark} onToggle={toggleTheme} />
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="miko-btn-ghost"
                  aria-expanded={mobileMenuOpen}
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className={`md:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
              <div className="px-2 pt-2 pb-3 space-y-1 border-t miko-divider">
                <NavLink to="/" end onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>📊 {t('nav.dashboard')}</NavLink>
                <NavLink to="/expenses" onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>💰 {t('nav.expenses')}</NavLink>
                <NavLink to="/budget" onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>📋 {t('nav.budget')}</NavLink>
                <div className="border-t miko-divider mt-2 pt-2 px-3 py-2">
                  <span className="block text-sm miko-text mb-2">{userInfo.name}</span>
                  <button onClick={logout} className="miko-btn-danger w-full">{t('nav.logout')}</button>
                </div>
              </div>
            </div>
          </div>
        </nav>

        <main className="flex-1 min-h-0 overflow-y-auto">
          <ChunkErrorBoundary
            message={t('app.chunkFailed')}
            retryLabel={t('app.reload')}
          >
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <DashboardLanding />
              </div>
            } />
            <Route path="/expenses" element={
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <ExpenseManagement />
              </div>
            } />
            <Route path="/budget" element={<AppLayout />}>
              <Route element={<BudgetPage />}>
                <Route index element={<Navigate to="gesamtuebersicht" replace />} />
                <Route path="monat" element={<BudgetSummaryCard />} />
                <Route path="jahr" element={<YearlyBudgetAnalysis />} />
                <Route path="depot" element={<DepotTab />} />
                <Route path="einnahmen" element={<IncomeTab />} />
                <Route path="kredite" element={<LoansTab />} />
                <Route path="sonderausgaben" element={<SonderausgabenPlanning />} />
                <Route path="gesamtuebersicht" element={<GesamtuebersichtPage />} />
                <Route path="kategorien" element={<CategoryManagement />} />
                <Route path="settings" element={<BudgetSettings />} />
              </Route>
            </Route>
          </Routes>
          </Suspense>
          </ChunkErrorBoundary>
        </main>
      </div>
    </Router>
  );
}

export default App;
